import {
  Button,
  Link,
  Stack,
  Typography,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Toolbar,
  OutlinedInput,
  InputAdornment,
  IconButton,
  Select,
  MenuItem,
  LinearProgress,
  SelectChangeEvent,
  Box,
  useTheme,
} from '@mui/material';
import { useCallback, useMemo, useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';

import { ExtractParams } from '@/types/params';

import { useGetAllBuildings } from '@/api/endpoints/building/useGetAllBuildings';
import {
  ApiResponse_GetAll_Tenant,
  useGetAllTenants,
} from '@/api/endpoints/tenant/useGetAllTenants';
import { useSetTenantAsPrimary } from '@/api/endpoints/tenant/useSetTenantAsPrimary';
import { useGetAllUnitsInBuilding } from '@/api/endpoints/unit/useGetAllUnitsInBuilding';
import { ChevronLeftIcon } from '@/assets/icons/ChevronLeftIcon';
import { ChevronRightIcon } from '@/assets/icons/ChevronRightIcon';
import { PlusIcon } from '@/assets/icons/PlusIcon';
import { SearchMdIcon } from '@/assets/icons/SearchMdIcon';
import { Star01Icon } from '@/assets/icons/Star01Icon';
import { PMPRoutes } from '@/config/routes';
import { usePropertyManager } from '@/context/PropertyManagerProvider';
import { formatFullDate } from '@/utils/date';
import { formatPhoneNumber } from '@/utils/phone';
import { getResolvedRoutePath } from '@/utils/router';

import RowActionsMenu from '@/components/common/RowActionsMenu';
import InviteTenantModal from '@/components/tenant/InviteTenantModal';
import TenantCreateModal from '@/components/tenant/TenantCreateModal';
import TenantDeleteModal from '@/components/tenant/TenantDeleteModal';
import TenantLeaseGeneratorModal from '@/components/tenant/TenantLeaseGeneratorModal';
import TenantUpdateModal from '@/components/tenant/TenantUpdateModal';

type Order = 'asc' | 'desc';
type Tenant = ApiResponse_GetAll_Tenant[0];

const TABLE_HEAD = [
  { id: 'full_name', label: 'Name', sortable: true, width: '25%' },
  { id: 'contacts', label: 'Contact Info', sortable: false, width: '35%' },
  { id: 'status', label: 'Status', sortable: true, width: '15%' },
  { id: 'created_at', label: 'Date Onboarded', sortable: true, width: '15%' },
];

function descendingComparator(a: Tenant, b: Tenant, orderBy: keyof Tenant) {
  if (orderBy === 'full_name' || orderBy === 'status') {
    const aValue = String(a[orderBy] ?? '').toLowerCase();
    const bValue = String(b[orderBy] ?? '').toLowerCase();
    if (bValue < aValue) return -1;
    if (bValue > aValue) return 1;
    return 0;
  }
  if (orderBy === 'created_at') {
    const aDate = new Date(String(a[orderBy] ?? '')).getTime();
    const bDate = new Date(String(b[orderBy] ?? '')).getTime();
    return bDate - aDate;
  }
  const aValue = a[orderBy] ?? '';
  const bValue = b[orderBy] ?? '';
  if (bValue < aValue) return -1;
  if (bValue > aValue) return 1;
  return 0;
}

function getComparator(order: Order, orderBy: keyof Tenant) {
  return order === 'desc'
    ? (a: Tenant, b: Tenant) => descendingComparator(a, b, orderBy)
    : (a: Tenant, b: Tenant) => -descendingComparator(a, b, orderBy);
}

const UnitViewTenantListSubpage: React.FC = () => {
  const { id } = useParams<ExtractParams<PMPRoutes.unitView>>();
  const routeParams = useParams<ExtractParams<PMPRoutes.propertyLocationViewTenantList>>();
  const { locationId } = routeParams;
  const location = useLocation();
  const { isSingleFamilyLocation } = usePropertyManager();
  const theme = useTheme();

  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<keyof Tenant>('full_name');
  const [filterName, setFilterName] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [isTenantCreateModalOpen, setIsTenantCreateModalOpen] = useState(false);
  const [isTenantDeleteModalOpen, setIsTenantDeleteModalOpen] = useState(false);
  const [isTenantUpdateModalOpen, setIsTenantUpdateModalOpen] = useState(false);
  const [isTenantLeaseModalOpen, setIsTenantLeaseModalOpen] = useState(false);
  const [isInviteTenantModalOpen, setIsInviteTenantModalOpen] = useState(false);

  const [tenantToUpdate, setTenantToUpdate] = useState<Tenant | null>(null);
  const [tenantToDelete, setTenantToDelete] = useState<Tenant | null>(null);
  const [tenantForLease, setTenantForLease] = useState<Tenant | null>(null);
  const [unitId, setUnitId] = useState<number | undefined>(id ? Number(id) : undefined);

  // For single family homes, get the first unit ID if not provided
  const getAllBuildingsQuery = useGetAllBuildings(
    {
      location_id: Number(locationId),
    },
    !!locationId && isSingleFamilyLocation && !unitId
  );

  const getAllUnitsQuery = useGetAllUnitsInBuilding(
    {
      buildingId: getAllBuildingsQuery.data?.[0]?.id,
    },
    !!getAllBuildingsQuery.data?.[0]?.id && isSingleFamilyLocation && !unitId
  );

  // Set the unit ID when we get it for single family homes
  useEffect(() => {
    if (isSingleFamilyLocation && !unitId && getAllUnitsQuery.data?.[0]?.id) {
      setUnitId(getAllUnitsQuery.data[0].id);
    }
  }, [isSingleFamilyLocation, unitId, getAllUnitsQuery.data]);

  const getAllTenantsQuery = useGetAllTenants({
    unit_id: unitId,
    ...(locationId && { location_id: Number(locationId) }),
  });

  const setTenantAsPrimaryMutation = useSetTenantAsPrimary();

  const handleRequestSort = (property: keyof Tenant) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleFilterName = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilterName(event.target.value);
    setPage(0);
  };

  const handleChangeRowsPerPage = (event: SelectChangeEvent<number>, _: React.ReactNode) => {
    setRowsPerPage(Number(event.target.value));
    setPage(0);
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Filter and sort the data
  const filteredData = useMemo(() => {
    let filtered = getAllTenantsQuery.data || [];

    // Apply name filter
    if (filterName) {
      filtered = filtered.filter(item =>
        item.full_name.toLowerCase().includes(filterName.toLowerCase())
      );
    }

    // Sort the data
    return [...filtered].sort(getComparator(order, orderBy));
  }, [getAllTenantsQuery.data, filterName, order, orderBy]);

  // Get the current page of data
  const displayedData = useMemo(
    () => filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredData, page, rowsPerPage]
  );

  const onEditTenantClick = useCallback((tenant: Tenant) => {
    setTenantToUpdate(tenant);
    setIsTenantUpdateModalOpen(true);
  }, []);

  const onDeleteTenantClick = useCallback((tenant: Tenant) => {
    setTenantToDelete(tenant);
    setIsTenantDeleteModalOpen(true);
  }, []);

  const onSetTenantAsPrimaryClick = useCallback(
    (tenant: Tenant) => {
      setTenantAsPrimaryMutation.mutate(
        { tenantId: tenant.id },
        {
          onSuccess: () => {
            getAllTenantsQuery.refetch();
          },
        }
      );
    },
    [setTenantAsPrimaryMutation, getAllTenantsQuery]
  );

  // Auto-open tenant create modal if URL has openAddTenant=true
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const shouldOpenAddTenant = searchParams.get('openAddTenant') === 'true';
    const shouldOpenInviteTenant = searchParams.get('openInviteTenant') === 'true';

    if (shouldOpenAddTenant) {
      setIsTenantCreateModalOpen(true);
    }

    // Auto-open invite tenant modal if URL has openInviteTenant=true
    if (shouldOpenInviteTenant) {
      setIsInviteTenantModalOpen(true);
    }
  }, [location.search, locationId, unitId]);

  const onAddTenantClick = useCallback(() => {
    setIsTenantCreateModalOpen(true);
  }, []);

  return (
    <Stack direction="column" spacing={2} sx={{ flex: '1 1 100%', position: 'relative' }}>
      <Stack
        direction="row"
        spacing={1}
        sx={{
          alignSelf: 'flex-end',
          right: { sm: 0 },
        }}
      >
        <Button
          color="primary"
          variant="contained"
          size="medium"
          startIcon={<PlusIcon />}
          onClick={onAddTenantClick}
        >
          Add Tenant
        </Button>
      </Stack>

      <Card sx={{ bgcolor: 'common.white' }}>
        <Toolbar
          sx={{
            height: 64,
            display: 'flex',
            justifyContent: 'space-between',
            p: theme.spacing(2),
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'common.white',
          }}
        >
          <OutlinedInput
            fullWidth
            value={filterName}
            onChange={handleFilterName}
            placeholder="Search tenants..."
            startAdornment={
              <InputAdornment position="start">
                <SearchMdIcon sx={{ color: 'text.disabled' }} />
              </InputAdornment>
            }
            sx={{
              maxWidth: 320,
              bgcolor: 'common.white',
            }}
          />
        </Toolbar>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {TABLE_HEAD.map(headCell => (
                  <TableCell
                    key={headCell.id}
                    align={headCell.id === '' ? 'right' : 'left'}
                    sortDirection={orderBy === headCell.id ? order : false}
                    sx={{
                      width: headCell.width,
                      borderRight: 'none',
                      '&:last-child': {
                        borderRight: 'none',
                      },
                      bgcolor: theme.palette.secondary[200],
                      textTransform: 'none',
                      color: 'text.primary',
                      '& .MuiTableSortLabel-root': {
                        textTransform: 'none',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontSize: { xs: '0.875rem', sm: 'inherit' },
                        fontWeight: 'bold',
                        color: 'text.primary',
                        '&:hover': {
                          color: 'text.primary',
                        },
                        '&.Mui-active': {
                          color: 'text.primary',
                          '&:hover': {
                            color: 'text.primary',
                          },
                        },
                      },
                    }}
                  >
                    {headCell.sortable ? (
                      <TableSortLabel
                        active={orderBy === headCell.id}
                        direction={orderBy === headCell.id ? order : 'asc'}
                        onClick={() => handleRequestSort(headCell.id as keyof Tenant)}
                      >
                        {headCell.label}
                      </TableSortLabel>
                    ) : (
                      <Typography
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontSize: { xs: '0.875rem', sm: 'inherit' },
                          fontWeight: 'bold',
                          color: 'text.primary',
                          '&:hover': {
                            color: 'text.primary',
                          },
                        }}
                      >
                        {headCell.label}
                      </Typography>
                    )}
                  </TableCell>
                ))}
                <TableCell
                  align="right"
                  sx={{
                    width: '50px',
                    bgcolor: theme.palette.secondary[200],
                    borderRight: 'none',
                  }}
                />
              </TableRow>
            </TableHead>

            <TableBody>
              {getAllTenantsQuery.isLoading && (
                <TableRow>
                  <TableCell colSpan={TABLE_HEAD.length + 1} align="center" sx={{ py: 3 }}>
                    <LinearProgress sx={{ width: '100%' }} />
                  </TableCell>
                </TableRow>
              )}
              {!getAllTenantsQuery.isLoading && displayedData.length === 0 && (
                <TableRow>
                  <TableCell colSpan={TABLE_HEAD.length + 1} align="center" sx={{ py: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      No tenants found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {!getAllTenantsQuery.isLoading &&
                displayedData.length > 0 &&
                displayedData.map(row => (
                  <TableRow
                    key={String(row.id)}
                    sx={{
                      '&:hover': {
                        backgroundColor: `${theme.palette.secondary[200]} !important`,
                        cursor: 'pointer',
                        transition: 'background-color 0.2s ease',
                      },
                    }}
                  >
                    <TableCell sx={{ width: '25%' }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Link
                          href={getResolvedRoutePath(PMPRoutes.tenantView, { id: String(row.id) })}
                        >
                          {row.full_name}
                        </Link>
                        {row.metadata?.is_primary && (
                          <Star01Icon
                            sx={{
                              color: 'warning.main',
                              width: 16,
                              height: 16,
                            }}
                          />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ width: '35%' }}>
                      <Stack direction="column" spacing={1.25} sx={{ display: 'flex', flex: 1 }}>
                        {row.contacts.map(contact => (
                          <Stack direction="column" spacing={0.25} key={contact.id}>
                            {!!contact.email && (
                              <Typography variant="body4">{contact.email}</Typography>
                            )}
                            {!!contact.phone && (
                              <Typography variant="body4">
                                {formatPhoneNumber(contact.phone)}
                              </Typography>
                            )}
                          </Stack>
                        ))}
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ width: '15%' }}>{row.status || ''}</TableCell>
                    <TableCell sx={{ width: '15%' }}>
                      {formatFullDate(new Date(row.created_at || ''))}
                    </TableCell>
                    <TableCell align="right" sx={{ width: '50px' }}>
                      <RowActionsMenu
                        items={[
                          {
                            label: 'Edit',
                            onClick: () => onEditTenantClick(row),
                          },
                          ...(!row.metadata?.is_primary
                            ? [
                              {
                                label: 'Set as Primary',
                                onClick: () => onSetTenantAsPrimaryClick(row),
                              },
                            ]
                            : []),
                          {
                            label: 'Delete',
                            onClick: () => onDeleteTenantClick(row),
                          },
                        ]}
                      />
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: 'common.white',
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              Rows per page:
            </Typography>
            <Select
              value={rowsPerPage}
              onChange={handleChangeRowsPerPage}
              size="small"
              sx={{ width: 80 }}
              inputProps={{ 'aria-label': 'rows per page' }}
            >
              <MenuItem value={5}>5</MenuItem>
              <MenuItem value={10}>10</MenuItem>
            </Select>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              {`${page * rowsPerPage + 1}-${Math.min(
                (page + 1) * rowsPerPage,
                filteredData.length
              )} of ${filteredData.length}`}
            </Typography>
            <IconButton
              onClick={() => handleChangePage(null, page - 1)}
              disabled={page === 0}
              size="small"
            >
              <ChevronLeftIcon />
            </IconButton>
            <IconButton
              onClick={() => handleChangePage(null, page + 1)}
              disabled={page >= Math.ceil(filteredData.length / rowsPerPage) - 1}
              size="small"
            >
              <ChevronRightIcon />
            </IconButton>
          </Stack>
        </Box>
      </Card>

      {isTenantCreateModalOpen && locationId && (
        <TenantCreateModal
          onClose={() => setIsTenantCreateModalOpen(false)}
          unitId={unitId ?? 0}
          locationId={Number(locationId)}
          hasExistingTenants={(getAllTenantsQuery.data ?? []).length > 0}
          onTenantCreated={() => {
            getAllTenantsQuery.refetch();
          }}
        />
      )}

      {isTenantUpdateModalOpen && !!tenantToUpdate && (
        <TenantUpdateModal
          onClose={() => {
            setIsTenantUpdateModalOpen(false);
            setTenantToUpdate(null);
          }}
          tenant={tenantToUpdate}
        />
      )}

      {isTenantDeleteModalOpen && !!tenantToDelete && (
        <TenantDeleteModal
          onClose={() => {
            setIsTenantDeleteModalOpen(false);
            setTenantToDelete(null);
          }}
          tenant={tenantToDelete}
        />
      )}

      {isTenantLeaseModalOpen && !!tenantForLease && (
        <TenantLeaseGeneratorModal
          onClose={() => {
            setIsTenantLeaseModalOpen(false);
            setTenantForLease(null);
          }}
          tenant={tenantForLease}
        />
      )}

      {isInviteTenantModalOpen && locationId && (
        <InviteTenantModal
          locationId={Number(locationId)}
          unitId={unitId}
          onClose={() => {
            setIsInviteTenantModalOpen(false);
            const newUrl = new URL(window.location.href);
            newUrl.searchParams.delete('openInviteTenant');
            window.history.replaceState({}, '', newUrl.toString());
          }}
          onTenantsInvited={() => {
            getAllTenantsQuery.refetch();
            setIsInviteTenantModalOpen(false);
          }}
        />
      )}
    </Stack>
  );
};

export default UnitViewTenantListSubpage;
